import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import { getLocale } from "react-i18nify";

import { styled as muiStyled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Box,
  Typography,
  TablePagination,
  Stack,
  IconButton,
  InputAdornment,
  createFilterOptions,
  Autocomplete,
} from "@mui/material";

import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import EditIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import DiscountIcon from "@mui/icons-material/Discount";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import font from "src/styles/font";
import { Black, DarkGrey, Grey, Primary, White01 } from "src/styles/color";
import { useDialog } from "src/context/DialogContext";
import { hexToRgba } from "src/common/color";

import TextField from "src/components/core/TextField";
import Button from "src/components/core/Button";
import AddMultiProductDialog from "./AddMultiProductDialog";

import {
  deleteProductInGroup,
  findAllProductGroup,
  updateProductGroup,
} from "src/api/deal";
import { convertPrice } from "src/common";
import EditProductDialog from "./EditProductDialog";
import { useAuth } from "src/hooks/useAuth";
import CollapsibleTable from "src/components/core/CollapsibleTable";
import Loading from "src/components/ui/Loading";
import device from "src/styles/device";

const BoxWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  // display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;
`;

const ImageNoData = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/no+data.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;

const TextNoData = styled.pre`
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f2f1f3;
  border-radius: 8px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const ImgDisplay = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  object-position: center;
  border-radius: 8px;
  flex-shrink: 0;
`;

const TextOption = styled(Box)`
  width: 100%;
  color: ${({ color }) =>
    color === "grey"
      ? "var(--darkgrey-color)"
      : color === "primary"
        ? "var(--primary-color)"
        : "var(--secondary-color)"};
  font-size: ${font.size.sm};
  font-weight: 400;
  margin-bottom: 8px;
`;

const TextPriceOption = styled(Box)`
  width: 100%;
  color: var(--primary-color);
  font-size: ${font.size.md};
  font-weight: 500;
`;

const StyledInputAdornment = styled(InputAdornment)`
  margin: 0;

  &.MuiInputAdornment-root {
    font-size: ${font.size.sm};
    font-weight: 400;
    color: ${Grey};
  }
`;

const ButtonGroup = styled(Stack)`
  width: 100%;
  max-width: 1240px;
  height: 72px;
  background-color: var(--white-color);
  z-index: 15;
  margin-top: 24px;
`;

const drawerWidth = 260;

const Main = muiStyled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  position: "relative",
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    position: "relative",
  }),
}));

const DrawerHeader = muiStyled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
  height: "75px",
}));

function NoRowsOverlay() {
  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      <ImageNoData></ImageNoData>
      <TextNoData>No Data</TextNoData>
    </Stack>
  );
}

const DealGroupList = ({ open }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const { groupId } = useParams();

  const { openDialog, closeDialog, closeAllDialogs } = useDialog();

  const isSmallerThanDesktop = useMediaQuery(device.lcd);

  const [group, setGroup] = useState(null);
  const [products, setProducts] = useState({
    data: [],
    total: 0,
  });
  const [editProduct, setEditProduct] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [dataSearch, setDataSearch] = useState({
    page: 0,
    page_size: 10,
    sortBy: "updatedAt",
    sortOrder: "desc",
  });

  const columns = [
    {
      field: "product_choice_deal",
      label: "Product",
      width: "30%",
      sortable: false,
      align: "left",
      userRoles: [1],
      renderCell: (params) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {!!params?.row?.product_choice_deal?.group_image
              ?.product_display_image === false ? (
              <ImageEmpty>
                <InsertPhotoIcon
                  sx={{
                    color: "rgba(0, 0, 0, 0.54)",
                    cursor: "pointer",
                  }}
                />
              </ImageEmpty>
            ) : (
              <ImgDisplay
                src={
                  params?.row?.product_choice_deal?.group_image
                    ?.product_display_image
                  // ? params?.row?.product_choice_deal?.group_image?.product_display_image.replace(
                  //     ".com",
                  //     ".com/300x300"
                  //   )
                  // : ""
                }
                alt=""
              />
            )}
            <Stack
              gap={1}
              sx={{
                ml: 2,
                "& .MuiTypography-root": {
                  fontSize: font.size.sm,
                },
                "& span.MuiTypography-root": {
                  color: DarkGrey,
                },
              }}
            >
              <Typography component="p">
                {params?.row?.product_choice_deal?.product?.item_name}
              </Typography>
              <Typography component="span">
                {params?.row?.product_choice_deal?.product?.item_code}
              </Typography>
              <Typography component="span">
                {params?.row?.product_choice_deal?.size?.quantity === "size"
                  ? `${params?.row?.product_choice_deal?.size?.width} ${
                      getLocale() === "th"
                        ? params?.row?.product_choice_deal?.size?.unit_width
                            ?.unit_th
                        : params?.row?.product_choice_deal?.size?.unit_width
                            ?.unit_en
                    } x ${params?.row?.product_choice_deal?.size?.height} ${
                      getLocale() === "th"
                        ? params?.row?.product_choice_deal?.size?.unit_height
                            ?.unit_th
                        : params?.row?.product_choice_deal?.size?.unit_height
                            ?.unit_en
                    } x ${params?.row?.product_choice_deal?.size?.thickness} ${
                      getLocale() === "th"
                        ? params?.row?.product_choice_deal?.size?.unit_thickness
                            ?.unit_th
                        : params?.row?.product_choice_deal?.size?.unit_thickness
                            ?.unit_en
                    }`
                  : params?.row?.product_choice_deal?.size?.quantity ===
                      "capacity"
                    ? `${params?.row?.product_choice_deal?.size?.capacity_size} ${
                        getLocale() === "th"
                          ? params?.row?.product_choice_deal?.size
                              ?.unit_capacity?.unit_th
                          : params?.row?.product_choice_deal?.size
                              ?.unit_capacity?.unit_en
                      }`
                    : getLocale() === "th"
                      ? "สินค้าสั่งทำพิเศษ"
                      : "Made by order"}
              </Typography>
              <Typography component="span">
                (
                {getLocale() === "th"
                  ? params?.row?.product_choice_deal?.color.color_th
                  : params?.row?.product_choice_deal?.color.color_en}
                )
              </Typography>
            </Stack>
          </Box>
        );
      },
    },
    {
      field: "company_name",
      label: "Shop",
      width: "13%",
      sortable: true,
      align: "center",
      userRoles: [1],
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            component="p"
            sx={{
              "&.MuiTypography-root": {
                fontSize: font.size.sm,
              },
            }}
          >
            {
              params?.row?.product_choice_deal?.product?.user?.seller
                ?.company_name
            }
          </Typography>
        </Box>
      ),
    },
    {
      field: "createdAt",
      label: "Created Date",
      width: "13%",
      sortable: true,
      align: "center",
      userRoles: [1],
      renderCell: (params) =>
        moment(params?.row?.product_choice_deal?.createdAt).format(
          "DD-MM-YYYY",
        ),
    },
    {
      field: "updatedAt",
      label: "Updated Date",
      width: "13%",
      sortable: true,
      align: "center",
      userRoles: [1],
      renderCell: (params) =>
        moment(params?.row?.product_choice_deal?.updatedAt).format(
          "DD-MM-YYYY",
        ),
    },
    {
      field: "price",
      label: "Price",
      width: "23%",
      sortable: true,
      align: "center",
      userRoles: [1],
      renderCell: (params) => (
        <Stack gap={1}>
          <Box>
            <TextField
              variant="outlined"
              fullWidth
              required
              value={params?.row?.price}
              sx={{
                ".MuiOutlinedInput-input": {
                  px: 2,
                  py: 1,
                  textAlign: "right",
                },
              }}
              InputProps={{
                startAdornment: (
                  <StyledInputAdornment position="start">
                    {isSmallerThanDesktop ? (
                      <AttachMoneyIcon
                        fontSize="12"
                        sx={{
                          color: Grey,
                          mr: 1,
                          width: "1.25em",
                          height: "1.25em",
                        }}
                      />
                    ) : (
                      <>
                        <EditIcon fontSize="10" sx={{ color: Grey, mr: 1 }} />
                        ราคาปกติ
                      </>
                    )}
                  </StyledInputAdornment>
                ),
              }}
              onChange={(e) => {
                if (e.target.value * 0 === 0) {
                  let dataChoice = editProduct;
                  let editIndex = editProduct.findIndex(
                    ({ _id }) => _id === params?.row?._id,
                  );
                  dataChoice[editIndex].price = e.target.value;
                  setEditProduct([...dataChoice]);
                }
              }}
              onBlur={(e) => {
                if (e.target.value * 0 === 0) {
                  let dataChoice = editProduct;
                  let editIndex = editProduct.findIndex(
                    ({ _id }) => _id === params?.row?._id,
                  );
                  dataChoice[editIndex].price = parseFloat(e.target.value || 0);
                  setEditProduct([...dataChoice]);
                }
              }}
              error={!params?.row?.price || params?.row?.price === 0}
            />
          </Box>

          <Box>
            <TextField
              variant="outlined"
              fullWidth
              required
              value={params?.row?.special_price || ""}
              sx={{
                ".MuiOutlinedInput-input": {
                  px: 2,
                  py: 1,
                  textAlign: "right",
                  color: Primary,
                },
              }}
              InputProps={{
                startAdornment: (
                  <StyledInputAdornment position="start">
                    {isSmallerThanDesktop ? (
                      <DiscountIcon
                        fontSize="12"
                        sx={{
                          color: Grey,
                          mr: 1,
                          width: "1.25em",
                          height: "1.25em",
                        }}
                      />
                    ) : (
                      <>
                        <EditIcon fontSize="10" sx={{ color: Grey, mr: 1 }} />
                        ราคาลด
                      </>
                    )}
                  </StyledInputAdornment>
                ),
              }}
              onChange={(e) => {
                if (e.target.value * 0 === 0) {
                  let dataChoice = editProduct;
                  let editIndex = editProduct.findIndex(
                    ({ _id }) => _id === params?.row?._id,
                  );
                  dataChoice[editIndex].special_price = e.target.value;
                  setEditProduct([...dataChoice]);
                }
              }}
              onBlur={(e) => {
                if (e.target.value * 0 === 0) {
                  let dataChoice = editProduct;
                  let editIndex = editProduct.findIndex(
                    ({ _id }) => _id === params?.row?._id,
                  );
                  dataChoice[editIndex].special_price =
                    parseFloat(e.target.value) || null;
                  setEditProduct([...dataChoice]);
                }
              }}
              // error={
              //   !params?.row?.special_price || params?.row?.special_price === 0
              // }
            />
          </Box>
        </Stack>
      ),
    },
    {
      field: "actions",
      label: "Delete",
      width: "8%",
      sortable: false,
      align: "center",
      userRoles: [1],
      renderCell: (params) => (
        <IconButton
          sx={{
            color: Black,
            backgroundColor: hexToRgba(White01, 0.4),
          }}
          onClick={() => {
            openDialog({
              type: "confirm",
              title: "คุณต้องการลบใช่หรือไม่?",
              onConfirm: () => {
                handleDeleteProduct(params?.row?.product_choice_deal?._id);
                closeDialog();
              },
              maxWidth: 342,
            });
          }}
        >
          <DeleteOutlineRoundedIcon />
        </IconButton>
      ),
    },
  ];

  const visibleColumns = columns.filter((col) =>
    col.userRoles.includes(user?.role_id),
  );

  const handlePageChange = (newPage) => {
    setDataSearch({ ...dataSearch, page: newPage });
  };

  const handleSort = (sortBy) => {
    setDataSearch({
      ...dataSearch,
      sortBy: sortBy,
      sortOrder:
        dataSearch.sortBy === sortBy && dataSearch.sortOrder === "asc"
          ? "desc"
          : "asc",
    });
  };

  // const onSearchButton = () => {
  //   setDataSearch({ ...dataSearch, page: 0 });
  //   (async () => {
  //     fetchGroups();
  //   })();
  // };

  const fetchGroups = async (isEdit = false) => {
    try {
      setIsLoading(true);
      const result = await findAllProductGroup(groupId, {
        ...dataSearch,
      });
      const { deal_group, rows, all } = result.data;

      if (products.data.length === 0 || isEdit) fetchAllProductDeal(all);

      setEditProduct(rows);
      setGroup(deal_group);
    } catch (err) {
      console.error("fetch groups", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllProductDeal = async (limit) => {
    try {
      const result = await findAllProductGroup(groupId, {
        ...dataSearch,
        page_size: limit,
      });
      const { rows, all } = result.data;
      setProducts({
        data: rows,
        total: all,
      });
    } catch (err) {
      console.error("fetch all", err);
    }
  };

  const handleOnCancel = () => {
    // setEditProduct(products.data);
    fetchGroups(false);
  };

  const handleDeleteProduct = async (id) => {
    try {
      setIsLoading(true);
      await deleteProductInGroup(group._id, id);

      await enqueueSnackbar("ลบสินค้าสำเร็จ", {
        variant: "success",
      });

      await fetchGroups(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      await updateProductGroup({
        _id: group?._id,
        product_choice_deals: editProduct.map((product) => ({
          ...product,
          product_choice_deal: product?.product_choice_deal?._id,
        })),
      });

      await enqueueSnackbar("เพิ่มสินค้าสำเร็จ", {
        variant: "success",
      });

      await fetchGroups(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!groupId) return;
    fetchGroups(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, dataSearch.page, dataSearch.sortBy, dataSearch.sortOrder]);

  // useEffect(() => {
  //   if (isEdit) fetchAllProductDeal();
  // }, [isEdit]);

  return (
    <>
      <Main open={open}>
        <DrawerHeader />
        <BoxWrapper>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                mr: 2,
              }}
            >
              <IconButton
                onClick={() => window.history.back()}
                sx={{
                  mr: 1,
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  mr: 2,
                }}
              >
                {group?.name}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: 2,
                  width: "100%",
                }}
              >
                <Autocomplete
                  // freeSolo
                  aria-required={true}
                  getOptionSelected={(option, value) =>
                    option?.product_choice_deal?._id ===
                    value?.product_choice_deal?._id
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?.product_choice_deal?._id ===
                    value?.product_choice_deal?._id
                  }
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      key={`product-group-${option._product_choice_deal?.id}`}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          py: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-around",
                          gap: 2,
                        }}
                      >
                        <Box>
                          {!!option?.product_choice_deal?.group_image
                            ?.product_display_image === false ? (
                            <ImageEmpty>
                              <InsertPhotoIcon
                                sx={{
                                  color: "rgba(0, 0, 0, 0.54)",
                                  cursor: "pointer",
                                }}
                              />
                            </ImageEmpty>
                          ) : (
                            <img
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "cover",
                                objectPosition: "center",
                                borderRadius: "8px",
                                marginRight: "8px",
                              }}
                              src={
                                option?.product_choice_deal?.group_image
                                  ?.product_display_image
                                  ? option?.product_choice_deal?.group_image?.product_display_image.replace(
                                      ".com",
                                      ".com/300x300",
                                    )
                                  : ""
                              }
                              alt={
                                option?.product_choice_deal?.group_image
                                  ?.product_display_image
                              }
                            />
                          )}
                        </Box>

                        <Box
                          sx={{
                            width: "15%",
                            maxWidth: "150px",
                          }}
                        >
                          <TextOption>
                            {option?.product_choice_deal?.product?.item_name}
                          </TextOption>

                          <TextOption color="grey" sx={{ mb: 0 }}>
                            {option?.product_choice_deal?.product?.item_code}
                          </TextOption>
                        </Box>

                        <Box
                          sx={{
                            width: "25%",
                            maxWidth: "300px",
                            textAlign: "center",
                          }}
                        >
                          <TextOption color="grey">
                            {
                              option?.product_choice_deal?.product?.user?.seller
                                ?.company_name
                            }
                          </TextOption>
                        </Box>

                        <Box
                          sx={{
                            width: "30%",
                            maxWidth: "300px",
                          }}
                        >
                          <TextOption color="grey">
                            {option?.product_choice_deal?.size?.quantity ===
                            "size"
                              ? `${option?.product_choice_deal?.size?.width} ${
                                  getLocale() === "th"
                                    ? option?.product_choice_deal?.size
                                        ?.unit_width?.unit_th
                                    : option?.product_choice_deal?.size
                                        ?.unit_width?.unit_en
                                } x ${
                                  option?.product_choice_deal?.size?.height
                                } ${
                                  getLocale() === "th"
                                    ? option?.product_choice_deal?.size
                                        ?.unit_height?.unit_th
                                    : option?.product_choice_deal?.size
                                        ?.unit_height?.unit_en
                                } x ${
                                  option?.product_choice_deal?.size?.thickness
                                } ${
                                  getLocale() === "th"
                                    ? option?.product_choice_deal?.size
                                        ?.unit_thickness?.unit_th
                                    : option?.product_choice_deal?.size
                                        ?.unit_thickness?.unit_en
                                }`
                              : option?.product_choice_deal?.size?.quantity ===
                                  "capacity"
                                ? `${
                                    option?.product_choice_deal?.size
                                      ?.capacity_size
                                  } ${
                                    getLocale() === "th"
                                      ? option?.product_choice_deal?.size
                                          ?.unit_capacity?.unit_th
                                      : option?.product_choice_deal?.size
                                          ?.unit_capacity?.unit_en
                                  }`
                                : getLocale() === "th"
                                  ? "สินค้าสั่งทำพิเศษ"
                                  : "Made by order"}
                          </TextOption>

                          <TextOption color="grey" sx={{ mb: 0 }}>
                            (
                            {getLocale() === "th"
                              ? option?.product_choice_deal?.color?.color_th
                              : option?.product_choice_deal?.color?.color_en}
                            )
                          </TextOption>
                        </Box>

                        <Box
                          sx={{
                            width: "15%",
                            maxWidth: "100px",
                            textAlign: "right",
                          }}
                        >
                          {!!option?.special_price ? (
                            <>
                              <TextOption
                                color="grey"
                                sx={{ mb: 0, textDecoration: "line-through" }}
                              >
                                {`${convertPrice(option?.price || 0)}`}
                              </TextOption>

                              <TextPriceOption>
                                {`${convertPrice(option?.special_price || 0)}`}
                              </TextPriceOption>
                            </>
                          ) : (
                            <TextOption>
                              {`${convertPrice(option?.price || 0)}`}
                            </TextOption>
                          )}
                        </Box>
                      </Box>
                    </li>
                  )}
                  options={products.data}
                  value={null}
                  onChange={(event, newValue) => {
                    openDialog({
                      type: "custom",
                      customDialog: (
                        <EditProductDialog
                          initialValue={newValue}
                          initialGroup={group}
                          onClose={() => closeAllDialogs()}
                          onSuccess={() => {
                            fetchGroups(true);
                            closeAllDialogs();
                          }}
                        />
                      ),
                    });
                  }}
                  getOptionLabel={(option) => {
                    return (
                      option?.product_choice_deal?.product?.item_name +
                      " - " +
                      option?.product_choice_deal?.product?.item_code +
                      (option?.product_choice_deal?.name !== ""
                        ? ` - (` + option?.product_choice_deal?.name + `)`
                        : ``) +
                      ` [${option?.product_choice_deal?.product?.user?.seller?.company_name}]`
                    );
                  }}
                  renderTags={(value) => null}
                  filterOptions={createFilterOptions({
                    matchFrom: "any",
                    limit: 100,
                  })}
                  sx={{
                    // mt: 2,
                    // mb: 3,
                    width: "100%",
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        maxWidth: "100%",
                        width: "100%",
                      }}
                      placeholder="ค้นหาโดย ชื่อ / รหัส / ร้านค้า"
                    />
                  )}
                />
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() =>
                openDialog({
                  type: "custom",
                  customDialog: (
                    <AddMultiProductDialog
                      initialGroup={group}
                      onClose={() => closeAllDialogs()}
                      onSuccess={() => {
                        fetchGroups(true);
                        closeAllDialogs();
                      }}
                    />
                  ),
                })
              }
              size="large"
              sx={{
                minWidth: 200,
              }}
            >
              เพิ่มสินค้า
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: 4,
            }}
          >
            <Typography
              sx={{
                mr: 2,
                color: DarkGrey,
              }}
              component="span"
            >
              {products.total} สินค้า
            </Typography>
          </Box>

          <CollapsibleTable
            rows={editProduct}
            collapsible={false}
            columns={visibleColumns}
            components={{
              NoRowsOverlay,
              // NoResultsOverlay,
            }}
            sortBy={dataSearch.sortBy}
            sortOrder={dataSearch.sortOrder}
            onSort={handleSort}
          />
          <TablePagination
            component="div"
            count={products.total}
            rowsPerPageOptions={[10]} // provide only 1 option to hide rows per page label
            rowsPerPage={10}
            page={dataSearch.page}
            onPageChange={(e, newPage) => handlePageChange(newPage)}
          />
        </BoxWrapper>

        <ButtonGroup
          flexDirection={"row"}
          justifyContent={"flex-end"}
          alignItems={"center"}
          columnGap={2}
        >
          <Button
            variant="secondary"
            size="large"
            onClick={() => {
              openDialog({
                type: "confirm",
                title: `คุณต้องการยกเลิกการแก้ไขใช่หรือไม่`,
                onConfirm: () => {
                  handleOnCancel();
                  closeDialog();
                },
                maxWidth: 342,
              });
            }}
            sx={{
              width: "100%",
              maxWidth: 160,
              borderRadius: 4,
            }}
          >
            ยกเลิก
          </Button>

          <Button
            variant="contained"
            size="large"
            onClick={() => {
              openDialog({
                type: "confirm",
                title: `คุณต้องการบันทึกการแก้ไขใช่หรือไม่`,
                onConfirm: () => {
                  handleSubmit();
                  closeDialog();
                },
                maxWidth: 342,
              });
            }}
            sx={{ width: "100%", maxWidth: 160, borderRadius: 4 }}
          >
            บันทึก
          </Button>
        </ButtonGroup>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default DealGroupList;
